import * as THREE from "three";
import { FOOD_TYPES, PECK_RANGE } from "@palomitas/shared";
import type { FoodType } from "@palomitas/shared";
import type { AABB } from "@palomitas/shared";

const PECK_HIT_RADIUS_MULTIPLIER = 1.25;
const PECK_FORWARD_RANGE = (PECK_RANGE / 3) * PECK_HIT_RADIUS_MULTIPLIER;
const PECK_LATERAL_TOLERANCE = 0.16 * PECK_HIT_RADIUS_MULTIPLIER;
const FOOD_REST_HEIGHT = 0.12;
const FOOD_COLLISION_RADIUS = 0.09;
const FOOD_TOSS_MIN_SPEED = 1.6;
const FOOD_TOSS_MAX_SPEED = 2.8;
const FOOD_TOSS_UP_SPEED = 2.4;
const FOOD_AIR_GRAVITY = 10.8;
const FOOD_AIR_BOUNCE_DAMPING = 0.58;
const FOOD_AIR_MIN_BOUNCE_SPEED = 0.7;
const TALON_GRAB_RADIUS = 0.42;
const TALON_GRAB_MAX_DROP = 1.15;
const TALON_HOLD_Y_OFFSET = 0.56;
const TALON_GROUNDED_DROP_SIDE_OFFSET = 0.28;

interface FoodInstance {
  type: FoodType;
  root: THREE.Group;
  core: THREE.Mesh;
  shards: THREE.Mesh[];
  pecksRemaining: number;
  totalPecks: number;
  collected: boolean;
  bobPhase: number;
  /** Y of the surface this item sits on; used so rooftop food bobs at roof height. */
  surfaceY: number;
  airborne: boolean;
  velocity: THREE.Vector3;
  held: boolean;
  tossOriginX: number;
  tossOriginZ: number;
  tossMaxDistance: number;
  source: "ambient" | "birdFeeder" | "kidDonut";
}

export interface FoodPeckResult {
  type: FoodType;
  fullyConsumed: boolean;
  hungerGain: number;
  source: "ambient" | "birdFeeder" | "kidDonut";
}

export class FoodSystem {
  private readonly items: FoodInstance[] = [];
  private readonly scene: THREE.Scene;
  private readonly rng: () => number;
  private readonly colliders: readonly AABB[];
  private heldItem: FoodInstance | null = null;

  constructor(
    scene: THREE.Scene,
    spawns: readonly THREE.Vector3[],
    rng: () => number,
    /** Elevated spawns (rooftops) — always produce rare, high-value food. */
    rooftopSpawns: readonly THREE.Vector3[] = [],
    colliders: readonly AABB[] = [],
  ) {
    this.scene = scene;
    this.rng = rng;
    this.colliders = colliders;
    for (const spawn of spawns)         this.addItem(scene, spawn, pickFoodType(rng),     rng() * Math.PI * 2);
    for (const spawn of rooftopSpawns)  this.addItem(scene, spawn, pickRareFoodType(rng), rng() * Math.PI * 2);
  }

  /** Spawn one or more ground-level breadcrumbs around a center point. */
  spawnBreadcrumbs(
    center: THREE.Vector3,
    count: number,
    source: "ambient" | "birdFeeder" | "kidDonut" = "ambient",
  ): void {
    const safeCount = Math.max(1, Math.min(8, Math.floor(count)));
    for (let i = 0; i < safeCount; i++) {
      const angle = this.rng() * Math.PI * 2;
      const dist = this.rng() * 0.54;
      const spawn = new THREE.Vector3(
        center.x + Math.cos(angle) * dist,
        Math.max(0, center.y),
        center.z + Math.sin(angle) * dist,
      );
      this.addItem(this.scene, spawn, getBreadcrumbType(), this.rng() * Math.PI * 2, source);
    }
  }

  private addItem(
    scene: THREE.Scene,
    spawn: THREE.Vector3,
    type: FoodType,
    bobPhase: number,
    source: "ambient" | "birdFeeder" | "kidDonut" = "ambient",
  ): FoodInstance {
    const totalPecks = requiredPecks(type);
    const { root, core, shards } = buildFoodVisual(type, totalPecks);
    root.position.set(spawn.x, spawn.y + FOOD_REST_HEIGHT, spawn.z);
    scene.add(root);
    const item: FoodInstance = {
      type,
      root,
      core,
      shards,
      pecksRemaining: totalPecks,
      totalPecks,
      collected: false,
      bobPhase,
      surfaceY: spawn.y,
      airborne: false,
      velocity: new THREE.Vector3(),
      held: false,
      tossOriginX: spawn.x,
      tossOriginZ: spawn.z,
      tossMaxDistance: 0,
      source,
    };
    this.items.push(item);
    return item;
  }

  update(dt: number, now: number, holderPosition: THREE.Vector3 | null = null): void {
    if (this.heldItem && holderPosition) {
      this.heldItem.root.position.set(
        holderPosition.x,
        holderPosition.y - TALON_HOLD_Y_OFFSET,
        holderPosition.z,
      );
      this.heldItem.root.rotation.y += dt * 1.6;
    }
    for (const item of this.items) {
      if (item.collected) continue;
      if (item.held) continue;
      item.root.rotation.y += dt * (item.airborne ? 2.1 : 0.8);
      if (item.airborne) {
        this.stepAirborne(item, dt);
        continue;
      }
      const bob = Math.sin(now * 0.002 + item.bobPhase) * 0.04;
      item.root.position.y = item.surfaceY + FOOD_REST_HEIGHT + bob;
    }
  }

  /**
   * Lets NPC scavengers "steal" breadcrumbs from the world.
   * Returns how many breadcrumb items were removed.
   */
  consumeBreadcrumbsNear(position: THREE.Vector3, radius: number, maxCount: number): number {
    const r2 = Math.max(0.05, radius) ** 2;
    const limit = Math.max(1, Math.floor(maxCount));
    let removed = 0;
    for (const item of this.items) {
      if (removed >= limit) break;
      if (item.collected || item.held || item.type.id !== "breadcrumb") continue;
      const dx = item.root.position.x - position.x;
      const dz = item.root.position.z - position.z;
      if (dx * dx + dz * dz > r2) continue;
      item.collected = true;
      item.root.visible = false;
      removed += 1;
    }
    return removed;
  }

  tryPeck(position: THREE.Vector3, yaw: number): FoodPeckResult | null {
    const forwardX = -Math.sin(yaw);
    const forwardZ = -Math.cos(yaw);
    const rightX = -forwardZ;
    const rightZ = forwardX;

    let closest: FoodInstance | null = null;
    let closestDist = PECK_FORWARD_RANGE * PECK_FORWARD_RANGE;
    for (const item of this.items) {
      if (item.collected || item.held) continue;
      // Don't peck food on a different floor (roof vs ground)
      if (Math.abs(item.root.position.y - position.y) > 1.5) continue;

      const dx = item.root.position.x - position.x;
      const dz = item.root.position.z - position.z;
      const forwardDist = dx * forwardX + dz * forwardZ;
      if (forwardDist <= 0 || forwardDist > PECK_FORWARD_RANGE) continue;

      const lateralDist = Math.abs(dx * rightX + dz * rightZ);
      if (lateralDist > PECK_LATERAL_TOLERANCE) continue;

      const d2 = dx * dx + dz * dz;
      if (d2 < closestDist) {
        closestDist = d2;
        closest = item;
      }
    }
    if (!closest) return null;

    closest.pecksRemaining -= 1;
    const hungerGain = closest.type.hungerGain / closest.totalPecks;
    if (closest.pecksRemaining <= 0) {
      closest.collected = true;
      closest.root.visible = false;
      return {
        type: closest.type,
        fullyConsumed: true,
        hungerGain,
        source: closest.source,
      };
    }

    applyDamageVisual(closest, this.rng);
    tossFood(closest, this.rng);
    return {
      type: closest.type,
      fullyConsumed: false,
      hungerGain,
      source: closest.source,
    };
  }

  remaining(): number {
    let count = 0;
    for (const item of this.items) if (!item.collected) count++;
    return count;
  }

  consumeNearestFoodByType(position: THREE.Vector3, typeId: FoodType["id"], radius: number): boolean {
    const r2 = Math.max(0.05, radius) ** 2;
    let best: FoodInstance | null = null;
    let bestD2 = r2;
    for (const item of this.items) {
      if (item.collected || item.held || item.type.id !== typeId) continue;
      const dx = item.root.position.x - position.x;
      const dz = item.root.position.z - position.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > bestD2) continue;
      best = item;
      bestD2 = d2;
    }
    if (!best) return false;
    best.collected = true;
    best.root.visible = false;
    best.airborne = false;
    best.velocity.set(0, 0, 0);
    best.held = false;
    return true;
  }

  spawnHeldFood(
    typeId: FoodType["id"],
    holderPosition: THREE.Vector3,
    source: "ambient" | "birdFeeder" | "kidDonut" = "ambient",
  ): FoodType | null {
    if (this.heldItem) return null;
    const type = FOOD_TYPES.find((entry) => entry.id === typeId);
    if (!type) return null;
    const spawn = new THREE.Vector3(
      holderPosition.x,
      holderPosition.y - TALON_HOLD_Y_OFFSET,
      holderPosition.z,
    );
    const item = this.addItem(this.scene, spawn, type, this.rng() * Math.PI * 2, source);
    item.held = true;
    item.airborne = false;
    item.velocity.set(0, 0, 0);
    item.tossMaxDistance = 0;
    item.root.position.copy(spawn);
    this.heldItem = item;
    return type;
  }

  spawnFoodAt(
    typeId: FoodType["id"],
    position: THREE.Vector3,
    source: "ambient" | "birdFeeder" | "kidDonut" = "ambient",
  ): FoodType | null {
    const type = FOOD_TYPES.find((entry) => entry.id === typeId);
    if (!type) return null;
    this.addItem(this.scene, position, type, this.rng() * Math.PI * 2, source);
    return type;
  }

  consumeNearestFood(position: THREE.Vector3, radius: number): boolean {
    const r2 = Math.max(0.05, radius) ** 2;
    let best: FoodInstance | null = null;
    let bestD2 = r2;
    for (const item of this.items) {
      if (item.collected || item.held) continue;
      const dx = item.root.position.x - position.x;
      const dz = item.root.position.z - position.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > bestD2) continue;
      best = item;
      bestD2 = d2;
    }
    if (!best) return false;
    best.collected = true;
    best.root.visible = false;
    best.airborne = false;
    best.velocity.set(0, 0, 0);
    best.held = false;
    return true;
  }

  tryGrabAirborne(position: THREE.Vector3): FoodType | null {
    if (this.heldItem) return null;
    const r2 = TALON_GRAB_RADIUS * TALON_GRAB_RADIUS;
    let closest: FoodInstance | null = null;
    let closestD2 = r2;
    for (const item of this.items) {
      if (item.collected || item.held) continue;
      const dx = item.root.position.x - position.x;
      const dz = item.root.position.z - position.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > closestD2) continue;
      const verticalDrop = position.y - item.root.position.y;
      if (verticalDrop < 0.02 || verticalDrop > TALON_GRAB_MAX_DROP) continue;
      closest = item;
      closestD2 = d2;
    }
    if (!closest) return null;
    closest.held = true;
    closest.airborne = false;
    closest.velocity.set(0, 0, 0);
    closest.tossMaxDistance = 0;
    this.heldItem = closest;
    return closest.type;
  }

  releaseHeldIfAny(dropNear: { position: THREE.Vector3; yaw: number } | null = null): FoodType | null {
    const held = this.heldItem;
    if (!held) return null;
    held.held = false;
    if (dropNear) {
      const rightX = Math.cos(dropNear.yaw);
      const rightZ = -Math.sin(dropNear.yaw);
      const dropX = dropNear.position.x + rightX * TALON_GROUNDED_DROP_SIDE_OFFSET;
      const dropZ = dropNear.position.z + rightZ * TALON_GROUNDED_DROP_SIDE_OFFSET;
      const surfaceY = getTopSurfaceAt(dropX, dropZ, dropNear.position.y + 0.6, this.colliders) ?? 0;
      held.root.position.set(
        dropX,
        surfaceY + FOOD_REST_HEIGHT,
        dropZ,
      );
      held.surfaceY = surfaceY;
      held.airborne = false;
      held.velocity.set(0, 0, 0);
      held.tossMaxDistance = 0;
    } else {
      held.airborne = true;
      held.velocity.set(0, -0.2, 0);
    }
    this.heldItem = null;
    return held.type;
  }

  private stepAirborne(item: FoodInstance, dt: number): void {
    item.velocity.y -= FOOD_AIR_GRAVITY * dt;

    const prevX = item.root.position.x;
    const prevY = item.root.position.y;
    const prevZ = item.root.position.z;

    let nextX = prevX + item.velocity.x * dt;
    const nextY = prevY + item.velocity.y * dt;
    let nextZ = prevZ + item.velocity.z * dt;

    for (const collider of this.colliders) {
      if (!intersectsWallAt(nextX, prevY, prevZ, collider)) continue;
      const resolvedX = resolveAxisWallCollision(
        prevX,
        nextX,
        collider.min.x,
        collider.max.x,
      );
      if (resolvedX !== null) {
        nextX = resolvedX;
        item.velocity.x = -item.velocity.x * FOOD_AIR_BOUNCE_DAMPING;
      }
      if (Math.abs(item.velocity.x) < FOOD_AIR_MIN_BOUNCE_SPEED) item.velocity.x = 0;
    }

    for (const collider of this.colliders) {
      if (!intersectsWallAt(nextX, prevY, nextZ, collider)) continue;
      const resolvedZ = resolveAxisWallCollision(
        prevZ,
        nextZ,
        collider.min.z,
        collider.max.z,
      );
      if (resolvedZ !== null) {
        nextZ = resolvedZ;
        item.velocity.z = -item.velocity.z * FOOD_AIR_BOUNCE_DAMPING;
      }
      if (Math.abs(item.velocity.z) < FOOD_AIR_MIN_BOUNCE_SPEED) item.velocity.z = 0;
    }

    if (item.tossMaxDistance > 0) {
      const dx = nextX - item.tossOriginX;
      const dz = nextZ - item.tossOriginZ;
      const d2 = dx * dx + dz * dz;
      const maxD = item.tossMaxDistance;
      if (d2 > maxD * maxD) {
        const d = Math.sqrt(d2);
        const scale = d > 1e-6 ? maxD / d : 0;
        nextX = item.tossOriginX + dx * scale;
        nextZ = item.tossOriginZ + dz * scale;
        item.velocity.x = 0;
        item.velocity.z = 0;
      }
    }

    item.root.position.set(nextX, nextY, nextZ);

    if (item.velocity.y > 0) return;

    const prevBottom = prevY - FOOD_REST_HEIGHT;
    const nextBottom = nextY - FOOD_REST_HEIGHT;
    const landingSurfaceY = getTopSurfaceAt(nextX, nextZ, prevBottom, this.colliders);
    if (landingSurfaceY === null) return;
    if (prevBottom + 1e-4 < landingSurfaceY || nextBottom - 1e-4 > landingSurfaceY) return;

    item.surfaceY = landingSurfaceY;
    item.root.position.y = landingSurfaceY + FOOD_REST_HEIGHT;
    item.velocity.set(0, 0, 0);
    item.airborne = false;
    item.tossMaxDistance = 0;
  }
}

/** Weighted random pick across all food types. */
function pickFoodType(rng: () => number): FoodType {
  const total = FOOD_TYPES.reduce((sum, t) => sum + t.rarity, 0);
  let roll = rng() * total;
  for (const type of FOOD_TYPES) {
    roll -= type.rarity;
    if (roll <= 0) return type;
  }
  return FOOD_TYPES[0];
}

/** Only picks from the rare, high-value food types (crust, donut, hot dog). */
function pickRareFoodType(rng: () => number): FoodType {
  const rare = FOOD_TYPES.filter(t => t.rarity > 0 && t.rarity <= 0.15);
  const total = rare.reduce((sum, t) => sum + t.rarity, 0);
  let roll = rng() * total;
  for (const type of rare) {
    roll -= type.rarity;
    if (roll <= 0) return type;
  }
  return rare[0]!;
}

function buildFoodMesh(type: FoodType): THREE.Mesh {
  const mat = new THREE.MeshLambertMaterial({ color: type.color });
  let geo: THREE.BufferGeometry;
  switch (type.id) {
    case "breadcrumb": geo = new THREE.IcosahedronGeometry(0.06, 0);            break;
    case "fry":        geo = new THREE.BoxGeometry(0.04, 0.04, 0.25);           break;
    case "crust":      geo = new THREE.BoxGeometry(0.22, 0.06, 0.12);           break;
    case "donut":      geo = new THREE.TorusGeometry(0.13, 0.05, 8, 16);        break;
    case "hotdog":     geo = new THREE.CapsuleGeometry(0.08, 0.2, 4, 8);        break;
    case "popcorn":    geo = new THREE.CylinderGeometry(0.13, 0.09, 0.22, 4);    break;
    default:           geo = new THREE.IcosahedronGeometry(0.08, 0);
  }
  const mesh = new THREE.Mesh(geo, mat);
  if (type.id === "popcorn") mesh.rotation.y = Math.PI * 0.25;
  mesh.castShadow = true;
  return mesh;
}

function requiredPecks(type: FoodType): number {
  switch (type.id) {
    case "breadcrumb": return 1;
    case "fry": return 2;
    case "crust": return 3;
    case "donut": return 5;
    case "hotdog": return 8;
    case "popcorn": return 6;
    default: return 1;
  }
}

function getBreadcrumbType(): FoodType {
  const hit = FOOD_TYPES.find((type) => type.id === "breadcrumb");
  return hit ?? FOOD_TYPES[0];
}

function buildFoodVisual(type: FoodType, totalPecks: number): {
  root: THREE.Group;
  core: THREE.Mesh;
  shards: THREE.Mesh[];
} {
  const root = new THREE.Group();
  const core = buildFoodMesh(type);
  root.add(core);
  if (type.id === "popcorn") {
    const stripeMat = new THREE.MeshLambertMaterial({ color: 0xf8f2df });
    for (const sx of [-0.052, -0.017, 0.017, 0.052]) {
      const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.016, 0.2, 0.145), stripeMat);
      stripe.position.set(sx, -0.005, 0);
      stripe.castShadow = true;
      root.add(stripe);
    }
    const kernelMat = new THREE.MeshLambertMaterial({ color: 0xf1de85 });
    const kernelGeo = new THREE.IcosahedronGeometry(0.028, 0);
    const kernels: Array<[number, number, number]> = [
      [-0.055, 0.132, -0.02],
      [-0.024, 0.145, 0.018],
      [0.012, 0.138, -0.008],
      [0.046, 0.144, 0.012],
      [-0.006, 0.158, -0.024],
      [0.03, 0.162, -0.026],
      [-0.038, 0.165, 0.002],
      [0.002, 0.176, 0.01],
    ];
    for (const [x, y, z] of kernels) {
      const kernel = new THREE.Mesh(kernelGeo, kernelMat);
      kernel.position.set(x, y, z);
      kernel.scale.set(1.0, 0.82, 0.95);
      kernel.castShadow = true;
      root.add(kernel);
    }
  }

  const shardCount = Math.max(0, totalPecks - 1);
  const shards: THREE.Mesh[] = [];
  for (let i = 0; i < shardCount; i++) {
    const shard = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.025 + (i % 3) * 0.006, 0),
      new THREE.MeshLambertMaterial({ color: type.color }),
    );
    shard.visible = false;
    shard.castShadow = true;
    root.add(shard);
    shards.push(shard);
  }
  return { root, core, shards };
}

function applyDamageVisual(item: FoodInstance, rng: () => number): void {
  const damageLevel = item.totalPecks - item.pecksRemaining;
  const damageT = damageLevel / item.totalPecks;

  // As food gets pecked apart, shrink and darken the core.
  const coreScale = 1 - damageT * 0.45;
  item.core.scale.setScalar(coreScale);
  item.core.rotation.x = (rng() - 0.5) * 0.35;
  item.core.rotation.z = (rng() - 0.5) * 0.35;

  const coreMat = item.core.material;
  if (coreMat instanceof THREE.MeshLambertMaterial) {
    coreMat.color.setHex(item.type.color).multiplyScalar(1 - damageT * 0.25);
  }

  for (let i = 0; i < item.shards.length; i++) {
    const shard = item.shards[i];
    if (!shard) continue;
    shard.visible = i < damageLevel;
    if (!shard.visible) continue;
    const angle = (i / Math.max(1, item.shards.length)) * Math.PI * 2 + rng() * 0.6;
    const radius = 0.07 + damageT * 0.08 + rng() * 0.03;
    shard.position.set(
      Math.cos(angle) * radius,
      -0.01 + damageT * 0.03 + (rng() - 0.5) * 0.02,
      Math.sin(angle) * radius,
    );
    shard.rotation.set(rng() * Math.PI, rng() * Math.PI, rng() * Math.PI);
    const mat = shard.material;
    if (mat instanceof THREE.MeshLambertMaterial) {
      mat.color.setHex(item.type.color).multiplyScalar(0.92 - damageT * 0.18);
    }
  }
}

function tossFood(item: FoodInstance, rng: () => number): void {
  const angle = rng() * Math.PI * 2;
  const distance = 0.44 + rng() * 0.44;
  const speed = FOOD_TOSS_MIN_SPEED + rng() * (FOOD_TOSS_MAX_SPEED - FOOD_TOSS_MIN_SPEED);
  item.velocity.x = Math.cos(angle) * speed;
  item.velocity.z = Math.sin(angle) * speed;
  item.velocity.y = FOOD_TOSS_UP_SPEED;
  item.airborne = true;
  item.tossOriginX = item.root.position.x;
  item.tossOriginZ = item.root.position.z;
  item.tossMaxDistance = distance;
  item.root.rotation.y += (rng() - 0.5) * 0.9;
}

function getTopSurfaceAt(
  x: number,
  z: number,
  maxSurfaceY: number,
  colliders: readonly AABB[],
): number | null {
  let top: number | null = 0;
  for (const box of colliders) {
    if (box.max.y > maxSurfaceY + 1e-4) continue;
    if (x <= box.min.x || x >= box.max.x || z <= box.min.z || z >= box.max.z) continue;
    if (top === null || box.max.y > top) top = box.max.y;
  }
  return top;
}

function intersectsWallAt(x: number, y: number, z: number, collider: AABB): boolean {
  const bottom = y - FOOD_REST_HEIGHT;
  const top = y + FOOD_COLLISION_RADIUS;
  if (top <= collider.min.y || bottom >= collider.max.y) return false;
  return (
    x + FOOD_COLLISION_RADIUS > collider.min.x &&
    x - FOOD_COLLISION_RADIUS < collider.max.x &&
    z + FOOD_COLLISION_RADIUS > collider.min.z &&
    z - FOOD_COLLISION_RADIUS < collider.max.z
  );
}

function resolveAxisWallCollision(
  prevAxis: number,
  nextAxis: number,
  min: number,
  max: number,
): number | null {
  const minFace = min - FOOD_COLLISION_RADIUS;
  const maxFace = max + FOOD_COLLISION_RADIUS;
  if (nextAxis <= minFace || nextAxis >= maxFace) return null;

  if (prevAxis <= minFace) return minFace;
  if (prevAxis >= maxFace) return maxFace;

  // If we started this step already inside the collider span on this axis,
  // eject to the nearest side to avoid snapping through to the opposite wall.
  const distToMin = Math.abs(nextAxis - minFace);
  const distToMax = Math.abs(maxFace - nextAxis);
  return distToMin <= distToMax ? minFace : maxFace;
}
